import { format } from "date-fns/format";
import { parseISO } from "date-fns/parseISO";

import formatLocaleNumber from "~/helpers/localeNumberFormat";
import moneyFormat from "~/helpers/moneyFormat";
import i18n from "~/i18n/i18n";
import { formatLineQuantities, truncateUnits } from "~fulfill/orders/helpers/orderLineUnits";

export interface ProposalField {
	label: string;
	value: string;
}

export interface ProposalLineItem {
	name: string;
	quantity?: string;
}

export interface ProposalSummary {
	fields: ProposalField[];
	items: ProposalLineItem[];
	rawArgs: string;
	title: string;
}

export interface ProposalItemUnit {
	eachesPerUnit: number;
	label: string;
}

export type NameLookup = (id: string) => string | undefined;
export type UnitLookup = (itemId: string) => ProposalItemUnit | undefined;

export interface ProposalNameResolver {
	customer?: NameLookup;
	item?: NameLookup;
	itemUnit?: UnitLookup;
}

const MAX_RAW = 4000;
const MAX_VALUE = 120;
const MAX_ITEMS = 25;
const MAX_FIELDS = 8;

const GUID_VALUE = /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32})$/i;
const CONTAINS_GUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\b[0-9a-f]{32}\b/i;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}/;
const TOOL_PREFIX = /^(?:fernie-pwa-|fernie-|wsapi[-_])/i;
const CREATE_ORDER = /create[-_]order/i;
const TRANSFER_INVENTORY_ITEM = /transfer[-_]inventory[-_]item/i;
const MODULE_SEGMENTS = new Set(["fulfill", "produce", "restock", "forecast", "core", "portal", "admin", "inventory"]);

const NAME_KEYS = ["name", "customername", "displayname", "fullname", "title", "label", "customer"];
const CUSTOMER_KEYS = ["customername", "customerdisplayname", "customer", "buyername", "accountname", "clientname"];
const DATE_KEYS = [
	"deliverydate",
	"expecteddeliverydate",
	"requesteddeliverydate",
	"deliveryon",
	"neededby",
	"duedate",
	"shipdate"
];
const TOTAL_KEYS = ["estimatedtotal", "ordertotal", "grandtotal", "totalamount", "total", "estimatedprice", "subtotal"];
const ITEMS_KEYS = ["lineitems", "items", "orderlines", "lines", "orderitems", "products", "details"];
const ITEM_NAME_KEYS = ["itemname", "productname", "description", "name", "label", "sku", "product", "item", "title"];
const QUANTITY_KEYS = ["quantity", "qty", "count", "units", "cases"];
const EACHES_KEY = "quantity";
const CUSTOMER_ID_KEYS = ["customerid"];
const ITEM_ID_KEYS = ["itemid", "productid"];

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const clamp = (value: string, max: number): string => (value.length > max ? `${value.slice(0, max)}…` : value);

const tryParse = (value: string): unknown => {
	try {
		return JSON.parse(value);
	} catch {
		return undefined;
	}
};

const lowerKeyed = (record: Record<string, unknown>): Map<string, unknown> => {
	const map = new Map<string, unknown>();
	for (const [key, value] of Object.entries(record)) map.set(key.toLowerCase(), value);
	return map;
};

const titleCase = (value: string): string =>
	value
		.split(/[-_\s]+/)
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(" ");

const humanizeKey = (key: string): string => titleCase(key.replace(/([a-z0-9])([A-Z])/g, "$1 $2"));

const humanizeToolLabel = (toolName: string): string => {
	const segments = toolName
		.replace(TOOL_PREFIX, "")
		.split(/[-_\s]+/)
		.filter(Boolean);
	if (segments.length > 1 && MODULE_SEGMENTS.has(segments[0].toLowerCase())) segments.shift();
	return titleCase(segments.join(" ")) || "Confirm Action";
};

const cleanString = (value: unknown): string | undefined => {
	if (typeof value !== "string") return undefined;
	const trimmed = value.trim();
	if (!trimmed || GUID_VALUE.test(trimmed) || CONTAINS_GUID.test(trimmed)) return undefined;
	return clamp(trimmed, MAX_VALUE);
};

const pickName = (record: Record<string, unknown>): string | undefined => {
	const fields = lowerKeyed(record);
	for (const key of NAME_KEYS) {
		const name = cleanString(fields.get(key));
		if (name) return name;
	}
	return undefined;
};

const resolveName = (value: unknown): string | undefined => {
	const direct = cleanString(value);
	if (direct) return direct;
	if (isRecord(value)) return pickName(value);
	return undefined;
};

const formatDate = (value: unknown): string | undefined => {
	if (typeof value !== "string") return undefined;
	const trimmed = value.trim();
	if (!trimmed || CONTAINS_GUID.test(trimmed)) return undefined;
	const candidate = DATE_ONLY.test(trimmed) ? trimmed.slice(0, 10) : trimmed;
	const parsed = parseISO(candidate);
	if (!Number.isNaN(parsed.getTime())) return format(parsed, "MMM d, yyyy");
	return clamp(trimmed, MAX_VALUE);
};

const formatTotal = (value: unknown): string | undefined => {
	if (typeof value === "number" && Number.isFinite(value)) return moneyFormat(value, "USD");
	if (typeof value !== "string") return undefined;
	const trimmed = value.trim();
	if (!trimmed || CONTAINS_GUID.test(trimmed)) return undefined;
	const numeric = Number(trimmed.replace(/[$,]/g, ""));
	if (Number.isFinite(numeric)) return moneyFormat(numeric, "USD");
	return clamp(trimmed, MAX_VALUE);
};

const pickQuantity = (fields: Map<string, unknown>): string | undefined => {
	for (const key of QUANTITY_KEYS) {
		const value = fields.get(key);
		if (typeof value === "number" && Number.isFinite(value)) return formatLocaleNumber(value);
		if (typeof value === "string") {
			const trimmed = value.trim();
			if (trimmed && !CONTAINS_GUID.test(trimmed)) return clamp(trimmed, MAX_VALUE);
		}
	}
	return undefined;
};

const pickItemName = (fields: Map<string, unknown>): string | undefined => {
	for (const key of ITEM_NAME_KEYS) {
		const name = cleanString(fields.get(key));
		if (name) return name;
	}
	return undefined;
};

const lookupById = <T>(
	fields: Map<string, unknown>,
	keys: string[],
	lookup?: (id: string) => T | undefined
): T | undefined => {
	if (!lookup) return undefined;
	for (const key of keys) {
		const id = fields.get(key);
		if (typeof id === "string" && id.trim()) {
			const found = lookup(id.trim());
			if (found !== undefined) return found;
		}
	}
	return undefined;
};

const resolveById = (fields: Map<string, unknown>, keys: string[], lookup?: NameLookup): string | undefined =>
	lookupById(fields, keys, lookup && ((id) => cleanString(lookup(id))));

const formatEaches = (fields: Map<string, unknown>, unitLookup?: UnitLookup): string | undefined => {
	const eaches = fields.get(EACHES_KEY);
	const unit = lookupById(fields, ITEM_ID_KEYS, unitLookup);
	if (!unit || typeof eaches !== "number" || !Number.isFinite(eaches)) return undefined;
	return formatLineQuantities(eaches, truncateUnits(eaches, unit.eachesPerUnit), unit.label);
};

const extractItems = (value: unknown, lookup?: NameLookup): ProposalLineItem[] => {
	if (!Array.isArray(value)) return [];
	const items: ProposalLineItem[] = [];
	for (const entry of value) {
		if (items.length >= MAX_ITEMS) break;
		if (!isRecord(entry)) {
			const name = cleanString(entry);
			if (name) items.push({ name });
			continue;
		}
		const fields = lowerKeyed(entry);
		const name = pickItemName(fields) ?? resolveById(fields, ITEM_ID_KEYS, lookup);
		const quantity = pickQuantity(fields);
		if (name) items.push(quantity ? { name, quantity } : { name });
		else if (quantity) items.push({ name: "Item", quantity });
	}
	return items;
};

const firstValue = <T>(fields: Map<string, unknown>, keys: string[], transform: (value: unknown) => T | undefined) => {
	for (const key of keys) {
		const result = transform(fields.get(key));
		if (result !== undefined) return result;
	}
	return undefined;
};

const buildCreateOrderSummary = (record: Record<string, unknown>, resolver?: ProposalNameResolver) => {
	const fields = lowerKeyed(record);
	const rows: ProposalField[] = [];
	const customer =
		firstValue(fields, CUSTOMER_KEYS, resolveName) ?? resolveById(fields, CUSTOMER_ID_KEYS, resolver?.customer);
	if (customer) rows.push({ label: "Customer", value: customer });
	const deliveryDate = firstValue(fields, DATE_KEYS, formatDate);
	if (deliveryDate) rows.push({ label: "Delivery date", value: deliveryDate });
	const total = firstValue(fields, TOTAL_KEYS, formatTotal);
	if (total) rows.push({ label: "Estimated total", value: total });
	const items = firstValue(fields, ITEMS_KEYS, (value) => {
		const extracted = extractItems(value, resolver?.item);
		return extracted.length > 0 ? extracted : undefined;
	});
	return { fields: rows, items: items ?? [] };
};

const renderScalar = (value: unknown): string | undefined => {
	if (typeof value === "string") return cleanString(value);
	if (typeof value === "number") return Number.isFinite(value) ? formatLocaleNumber(value) : undefined;
	if (typeof value === "boolean") return value ? "Yes" : "No";
	if (Array.isArray(value))
		return value.length > 0 ? `${value.length} ${value.length === 1 ? "item" : "items"}` : undefined;
	if (isRecord(value)) return pickName(value);
	return undefined;
};

const buildGenericSummary = (record: Record<string, unknown>): ProposalField[] => {
	const rows: ProposalField[] = [];
	for (const [key, value] of Object.entries(record)) {
		if (rows.length >= MAX_FIELDS) break;
		const rendered = renderScalar(value);
		if (rendered) rows.push({ label: humanizeKey(key), value: rendered });
	}
	return rows;
};

const isEachesProposal = (toolName: string): boolean => TRANSFER_INVENTORY_ITEM.test(toolName);

const buildEachesSummary = (record: Record<string, unknown>, resolver?: ProposalNameResolver) => {
	const fields = lowerKeyed(record);
	const item = resolveById(fields, ITEM_ID_KEYS, resolver?.item);
	const rows: ProposalField[] = item ? [{ label: i18n.t("fernieConfirm.item", { ns: "common" }), value: item }] : [];
	for (const [key, value] of Object.entries(record)) {
		if (rows.length >= MAX_FIELDS) break;
		const eaches = key.toLowerCase() === EACHES_KEY ? formatEaches(fields, resolver?.itemUnit) : undefined;
		const rendered = eaches ?? renderScalar(value);
		if (rendered) rows.push({ label: humanizeKey(key), value: rendered });
	}
	return { fields: rows, items: [] };
};

const buildRawArgs = (parsed: unknown, original: string): string => {
	if (parsed !== undefined) {
		try {
			return clamp(JSON.stringify(parsed, null, 2), MAX_RAW);
		} catch {
			return clamp(original.trim(), MAX_RAW);
		}
	}
	return clamp(original.trim(), MAX_RAW);
};

const summarizeProposal = (
	toolName: string,
	argumentsJson: string,
	resolver?: ProposalNameResolver
): ProposalSummary => {
	const title = humanizeToolLabel(toolName);
	const source = argumentsJson ?? "";
	const parsed = tryParse(source);
	const rawArgs = buildRawArgs(parsed, source);
	if (!isRecord(parsed)) return { fields: [], items: [], rawArgs, title };
	if (CREATE_ORDER.test(toolName)) {
		const { fields, items } = buildCreateOrderSummary(parsed, resolver);
		return { fields, items, rawArgs, title };
	}
	if (isEachesProposal(toolName)) {
		const { fields, items } = buildEachesSummary(parsed, resolver);
		return { fields, items, rawArgs, title };
	}
	return { fields: buildGenericSummary(parsed), items: [], rawArgs, title };
};

export default summarizeProposal;
